import { Employee } from "@finway-group/shared/lib/models"
import React, { createContext, useContext, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FullScreenLoader } from "~Components/FullScreenLoader"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { fetchAllEmployees } from "~Shared/store/actions/employee/employeeActions"
import { type RootState } from "~Shared/store/rootState.interface"

const LoggedInEmployeeContext = createContext<Employee | undefined>(undefined)

export const LoggedInEmployeeProvider = ({ children }: { children: React.ReactNode }) => {
    const { companyId, userId, isAuthenticated } = useAuth()
    const dispatch = useDispatch()
    const employeesStore = useSelector(({ employees }: RootState) => employees)

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchAllEmployees())
        }
    }, [isAuthenticated])

    const employeeState = employeesStore.items.find((e) => e.id === userId)
    const employee = useMemo<Employee | undefined>(() => {
        const employeeInternal = employeeState ? new Employee(employeeState) : undefined
        if (companyId && employeeInternal) {
            employeeInternal.setActiveCompanyProfile(companyId)
        }
        return employeeInternal
    }, [employeeState?.id, companyId])
    if (!employee) {
        return <FullScreenLoader />
    }
    return <LoggedInEmployeeContext.Provider value={employee}>{children}</LoggedInEmployeeContext.Provider>
}

/**
 * Returns the logged-in Employee, guaranteed to be non-null.
 * Must be used within a component tree wrapped by LoggedInEmployeeProvider (i.e. inside PrivateRoute).
 */
export const useRequiredLoggedInEmployee = (): Employee => {
    const employee = useContext(LoggedInEmployeeContext)
    if (!employee) {
        throw new Error("useRequiredLoggedInEmployee must be used within LoggedInEmployeeProvider")
    }
    return employee
}
